
import React from 'react';
import { Messages } from '../../../../../gen-src/Localize';
import { DatabaseTableSortOrder, WorkbookUpstreamTablesQueryVariables, WorkbookUpstreamTablesQuery, TablesDisplayGridRowDataFragment, UpstreamTablesFilteredByFieldQueryVariables, UpstreamTablesFilteredByFieldQuery , UpstreamTablesFilteredByFieldDocument , WorkbookUpstreamTablesDocument } from '../../../../../gen-src/queries';
import {
  ConnectionListResult,
  GridSortProps,
  MaxCountsResultData,
  PaginatedQueryWrapper,
} from '../../../data-catalog-shared';
import { TablesDisplayGrid } from '../sharedDisplayGrid/TablesDisplayGrid';
import { TablesValidSortFields } from '../sorting/sortProviders/TablesGridSortProvider';
import { getRowDataWithMaxCountsForTable } from '../table/functions/TableRowDataFunctions';
import { TablesDisplayGridRowData } from '../table/types/TablesDisplayGridRowData';

type QueryVariables = WorkbookUpstreamTablesQueryVariables;
type QueryResults = WorkbookUpstreamTablesQuery;
type QueryResultListNode = TablesDisplayGridRowDataFragment;
type GridRowData = TablesDisplayGridRowData;

type QueryVariablesWithFilter = UpstreamTablesFilteredByFieldQueryVariables;
type QueryResultsWithFilter = UpstreamTablesFilteredByFieldQuery;

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  return resultData && resultData.workbooks && resultData.workbooks[0] && resultData.workbooks[0].upstreamTablesConnection;
}

function selectConnectionListResultForFilteredQuery(resultData: QueryResultsWithFilter):
  ConnectionListResult<QueryResultListNode> | null {
  return resultData && resultData.fieldsConnection && resultData.fieldsConnection.nodes &&
    resultData.fieldsConnection.nodes[0] && resultData.fieldsConnection.nodes[0].upstreamTablesConnection;
}

function selectMaxCountsResult(resultData: QueryResults): MaxCountsResultData {
  return resultData && resultData.workbooks && resultData.workbooks[0];
}

function selectMaxCountsResultForFilteredQuery(resultData: QueryResultsWithFilter): MaxCountsResultData {
  return resultData && resultData.fieldsConnection &&
    resultData.fieldsConnection.nodes && resultData.fieldsConnection.nodes[0];
}

function getRowDataForTable(
  table: TablesDisplayGridRowDataFragment,
  resultData: QueryResults,
): TablesDisplayGridRowData {
  return getRowDataWithMaxCountsForTable(table, selectMaxCountsResult(resultData));
}

function getRowDataForTableForFilteredQuery(
  table: TablesDisplayGridRowDataFragment,
  resultData: QueryResultsWithFilter,
): TablesDisplayGridRowData {
  return getRowDataWithMaxCountsForTable(table, selectMaxCountsResultForFilteredQuery(resultData));
}

export type WorkbookUpstreamTablesQueryGridProps = GridSortProps<TablesValidSortFields> & {
  readonly vizportalUrlId: string;
  readonly filteredFieldId: string | null;
  readonly rowCount: number | null;
};

export function WorkbookUpstreamTablesQueryGrid(props: WorkbookUpstreamTablesQueryGridProps): JSX.Element {
  const emptyGridMessage = Messages.EmptyUpstreamTablesLineageGridMessage();
  const emptyFilteredGridMessage = Messages.EmptyUpstreamFilteredTablesLineageGridMessage();

  return props.filteredFieldId ? (
    // Query with Selected Field as Filter
    <PaginatedQueryWrapper<QueryVariablesWithFilter, QueryResultsWithFilter, QueryResultListNode, GridRowData>
      queryGql={UpstreamTablesFilteredByFieldDocument}
      partialQueryVariables={{
        selectedFieldId: props.filteredFieldId,
        orderBy: props.orderBy as DatabaseTableSortOrder,
      }}
      selectConnectionListResult={selectConnectionListResultForFilteredQuery}
      mapResultNode={getRowDataForTableForFilteredQuery}
    >
      {({ rowStore }) =>
        <TablesDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyFilteredGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />
      }
    </PaginatedQueryWrapper>
  ) : (
    // Query without Selection
    <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
      queryGql={WorkbookUpstreamTablesDocument}
      partialQueryVariables={{
        workbookVizportalUrlId: props.vizportalUrlId,
        orderBy: props.orderBy as DatabaseTableSortOrder,
      }}
      selectConnectionListResult={selectConnectionListResult}
      mapResultNode={getRowDataForTable}
    >
      {({ rowStore }) =>
        <TablesDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />}
    </PaginatedQueryWrapper>
  );
}
